/**
 * 로어북(월드 인포) 단독 import/export — 결정 #6. 카드 임베드와 별개로 로어북을
 * JSON 파일로 주고받는다(ST/Risu의 world info 공유 포맷과 호환).
 *
 * 매핑은 카드 경로와 동일 함수(mapLorebook / lorebookToCcv3)를 재사용해 일관성을
 * 보장한다. 플랫폼 중립(결정 #10): 바이트/문자열 ↔ 도메인 엔티티 변환만.
 */
import type { Lorebook } from "../domain/index.js";
import { mapLorebook } from "./card.js";
import { lorebookToCcv3 } from "./card-export.js";

type AnyObj = Record<string, unknown>;

/**
 * JSON 텍스트에서 로어북을 임포트한다. 지원 형태(결정 #6):
 *  - 단독 world info 객체: `{ name?, entries: [...] | {...} }`
 *  - 우리 export 래퍼: `{ spec: "lorebook_v1", data: {...} }`
 *  - 캐릭터 카드: `data.character_book`를 꺼내 사용(편의).
 */
export function importLorebookFromJson(text: string): Lorebook {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error("로어북 JSON을 해석할 수 없습니다.");
  }
  const root = parsed as AnyObj;

  // 우리 export 래퍼.
  if (root["spec"] === "lorebook_v1" && root["data"] && typeof root["data"] === "object") {
    return mapLorebook(root["data"] as AnyObj);
  }
  // 카드 안의 character_book.
  const cardData = (root["data"] && typeof root["data"] === "object" ? root["data"] : root) as AnyObj;
  if (cardData["character_book"] && typeof cardData["character_book"] === "object") {
    return mapLorebook(cardData["character_book"] as AnyObj);
  }
  // 단독 world info 객체(entries 보유).
  if (root["entries"] && typeof root["entries"] === "object") {
    return mapLorebook(root);
  }
  throw new Error("유효한 로어북 데이터를 찾을 수 없습니다(entries 없음).");
}

/** 로어북을 단독 JSON 문자열로 직렬화(파일 저장용, 결정 #6). */
export function exportLorebookToJson(book: Lorebook): string {
  return JSON.stringify({ spec: "lorebook_v1", data: lorebookToCcv3(book) }, null, 2);
}
